// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/UniversalTextField/TextSpaceObserver/index.jsx

import { Component } from 'react';

import { createAbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsolutePoint';
import { AbsolutePoint } from '@app/features/apply-react-signing/components/FieldRenderer/types';

import FontSizeNormalizer from '../FontSizeNormalizer';

import './TextSpaceObserver.scss';

interface TextSpaceObserverProps {
  fontSizes: [number] | [number, number];
  text: string | null;
  style: Pick<
    React.CSSProperties,
    'fontFamily' | 'height' | 'letterSpacing' | 'lineHeight' | 'textAlign' | 'width'
  >;
  onObserve: (prevRemain: AbsolutePoint, nextRemain: AbsolutePoint) => void;
}

/**
 * 공간박스안에 텍스트가 들어갈때에 남는 공간을 측정하는 컴포넌트 입니다.
 * 측정할 폰트 사이즈들을 배열에 담은 fontSizes 와, 박스의 크기와 텍스트 스타일을 props 로 받아,
 * 박스에 텍스트가 들어가고 남는 공간을 {x, y}로 props.onObserve 를 통해 리턴합니다.
 */
class TextSpaceObserver extends Component<TextSpaceObserverProps> {
  private observerRefMap: Map<number, HTMLDivElement> = new Map();

  /**
   * range를 이용해 textNode 의 위치, 크기를 측정하고, 남은 공간의 크기를 반환합니다.
   * @param observerRef - 옵저버 ref
   * @returns {{x: number, y: number}} - 남는 공간 {x, y}
   */
  static getRemainedSpace(observerRef: HTMLDivElement) {
    const observerClientRect = observerRef.getBoundingClientRect();
    if (!observerRef.childNodes.length) {
      return createAbsolutePoint({ x: observerClientRect.width, y: observerClientRect.height });
    }
    const rangeHelper = document.createRange();
    rangeHelper.selectNodeContents(observerRef.childNodes[0]);
    const textRanges = Array.from(rangeHelper.getClientRects());
    const lastRowRange = textRanges[textRanges.length - 1];
    const longestRowRight = Math.max.apply(
      null,
      textRanges.map((range) => range.right)
    );

    const remainedSpaceX = observerClientRect.right - longestRowRight;
    const remainedSpaceY = observerClientRect.bottom - lastRowRange.bottom;

    return createAbsolutePoint({ x: remainedSpaceX, y: remainedSpaceY });
  }

  shouldComponentUpdate(nextProps: TextSpaceObserverProps) {
    const { text, fontSizes, style: prevStyle, onObserve } = this.props;

    const { style: nextStyle } = nextProps;

    // props의 값이 변하지 않았다면 update하지 않도록 합니다.
    if (
      !fontSizes.find((v, i) => nextProps.fontSizes[i] !== v) &&
      prevStyle.height === nextStyle.height &&
      prevStyle.letterSpacing === nextStyle.letterSpacing &&
      prevStyle.lineHeight === nextStyle.lineHeight &&
      text === nextProps.text &&
      prevStyle.textAlign === nextStyle.textAlign &&
      prevStyle.width === nextStyle.width &&
      onObserve === nextProps.onObserve
    ) {
      return false;
    }
    return true;
  }

  componentDidUpdate() {
    const { fontSizes, onObserve } = this.props;

    const remainedSpaces = fontSizes
      .map((size) => this.observerRefMap.get(size))
      .filter((element): element is HTMLDivElement => element !== undefined)
      .map((element) => TextSpaceObserver.getRemainedSpace(element));

    onObserve(remainedSpaces[0], remainedSpaces[1]);
  }

  render() {
    const { fontSizes, text, style } = this.props;

    /** 텍스트 끝의 줄바꿈 문자가 공간을 차지하지않아 해당 경우엔 "."을 추가하여 측정 */
    const textReplacedLastBreakToDot = text && text.replace(/\n$/, '\n.');

    return fontSizes.map((size, index) => (
      <div
        className="TextSpaceObserver"
        key={index} // eslint-disable-line react/no-array-index-key
        style={style}
      >
        <FontSizeNormalizer
          fontSize={size}
          render={(_style) => (
            <div
              ref={(ref) => {
                if (ref) {
                  this.observerRefMap.set(size, ref);
                }
              }}
              style={_style}
            >
              {textReplacedLastBreakToDot}
            </div>
          )}
        />
      </div>
    ));
  }
}

export default TextSpaceObserver;
